// Modal registry — maps a modal-stack entry's `type` to the lazy-loaded
// wrapper component that owns its prop-wiring. Adding a new modal means
// adding one line here plus one co-located `<Component>Entry.jsx` next to
// the modal component itself — App.jsx never needs to change.
// NOT React.lazy — same signature, but a chunk fetch that never settles is
// rejected rather than leaving the modal stack showing its Suspense-fallback
// backdrop with no box in it, indefinitely. See src/utils/lazyWithTimeout.js.
import { lazyWithTimeout as lazy } from '../utils/lazyWithTimeout.js';

// Loaders live in their own map (rather than inline inside `lazy(...)`) so
// `preloadModal()` below can invoke the exact same `import()` call ahead of
// a push — dynamic imports are cached by module specifier, so calling a
// loader early and letting React.lazy call it again later resolves the
// second call from cache instead of starting a fresh network fetch.
const MODAL_LOADERS = {
  'document-edit': () => import('../documents/DocumentEditorEntry.jsx'),
  'document-form': () => import('../components/documents/DocumentFormModalEntry.jsx'),
  'document-review': () => import('../documents/DocumentReviewModalEntry.jsx'),
  'document-detail': () => import('../components/documents/DocumentDetailModalEntry.jsx'),
  'document-approval': () => import('../components/documents/DocumentApprovalModalEntry.jsx'),
  'document-workflow-hub': () => import('../components/documents/DocumentWorkflowHubEntry.jsx'),
  'document-share': () => import('../components/documents/ShareDocumentModalEntry.jsx'),
  'document-readers': () => import('../components/documents/DocumentReadersPanelEntry.jsx'),
  'task-form': () => import('../components/tasks/TaskFormModalEntry.jsx'),
  'adhoc-task-detail': () => import('../components/tasks/AdhocTaskDetailEntry.jsx'),
  'obligation-task-detail': () => import('../components/tasks/ObligationTaskDetailEntry.jsx'),
  'domain-action-detail': () => import('../components/tasks/DomainActionDetailEntry.jsx'),
  'control-detail': () => import('../components/controls/ControlDetailModalEntry.jsx'),
  'control-passages': () => import('../components/controls/ControlPassagesModalEntry.jsx'),
  'reasoning-chain': () => import('../components/controls/ReasoningChainModalEntry.jsx'),
  'owner-propagation': () => import('../components/controls/OwnerPropagationDialogEntry.jsx'),
  'control-bulk-task': () => import('../components/controls/ControlBulkTaskModalEntry.jsx'),
  'control-doc-scan-start': () =>
    import('../components/controls/ControlDocScanStartModalEntry.jsx'),
  'control-doc-scan-review': () =>
    import('../components/controls/ControlDocScanReviewModalEntry.jsx'),
  'user-settings': () => import('../components/users/UserSettingsModalEntry.jsx'),
  'risk-detail': () => import('../components/risks/RiskDetailModalEntry.jsx'),
  'vulnerability-detail': () =>
    import('../components/vulnerabilities/VulnerabilityDetailModalEntry.jsx'),
  'vulnerability-triage': () => import('../components/vulnerabilities/TriageActionModalEntry.jsx'),
  'vulnerability-promote': () =>
    import('../components/vulnerabilities/PromoteToRiskModalEntry.jsx'),
  'action-centre': () => import('../components/risks/ActionCentreModalEntry.jsx'),
  'comm-question': () => import('../components/communications/CommQuestionModalEntry.jsx'),
  'workflow-step': () => import('../components/workflows/WorkflowStepTaskModalEntry.jsx'),
  // PDW-HALT-2: the review of a halted recurring workflow — no release-flag or plan check.
  'workflow-halt-review': () => import('../components/workflows/WorkflowHaltReviewModalEntry.jsx'),
  // Behind the `riskAiAssessment` release flag (RPDA-04).
  'risk-ai-assess': () => import('../components/risks/ai/ReleasedAssessWithAiModalEntry.jsx'),
  changelog: () => import('../components/layout/ChangelogModalEntry.jsx'),
  'document-review-phases': () => import('../documents/DocumentReviewPhasesModalEntry.jsx'),
  'bia-wizard': () => import('../components/bia/BIAWizardEntry.jsx'),
  'bia-detail': () => import('../components/bia/BIADetailModalEntry.jsx'),
  'bcdr-plan-form': () => import('../components/bcdr/BCDRPlanModalEntry.jsx'),
  'bcdr-runbook-form': () => import('../components/bcdr/BCDRRunbookModalEntry.jsx'),
  'bcdr-incident-mode': () => import('../components/bcdr/BCDRIncidentModeModalEntry.jsx'),
  'bcdr-backup-form': () => import('../components/bcdr/BCDRBackupModalEntry.jsx'),
  'bcdr-drtest-form': () => import('../components/bcdr/BCDRDRTestModalEntry.jsx'),
  'bcdr-pentest-form': () => import('../components/bcdr/BCDRPenTestModalEntry.jsx'),
  'vendor-form': () => import('../components/vendors/VendorFormModalEntry.jsx'),
  'customer-form': () => import('../components/supplier/CustomerFormModalEntry.jsx'),
  'customer-detail': () => import('../components/supplier/CustomerDetailModalEntry.jsx'),
  'delivery-agreement-form': () =>
    import('../components/supplier/DeliveryAgreementFormModalEntry.jsx'),
  'delivery-agreement-detail': () =>
    import('../components/supplier/DeliveryAgreementDetailModalEntry.jsx'),
  'involvement-wizard': () => import('../components/supplier/InvolvementWizardModalEntry.jsx'),
  'involvement-detail': () => import('../components/supplier/InvolvementDetailModalEntry.jsx'),
  'new-involvement-picker': () =>
    import('../components/supplier/NewInvolvementPickerModalEntry.jsx'),
  'governance-initiative-detail': () =>
    import('../components/governanceInitiatives/GovernanceInitiativeDetailModalEntry.jsx'),
  'vendor-detail': () => import('../components/vendors/VendorDetailModalEntry.jsx'),
  'vendor-assessment': () => import('../components/vendors/VendorAssessmentModalEntry.jsx'),
  'employee-form': () => import('../components/employees/EmployeeFormModalEntry.jsx'),
  'employee-detail': () => import('../components/employees/EmployeeDetailModalEntry.jsx'),
  'employee-import-export': () =>
    import('../components/employees/EmployeeImportExportModalEntry.jsx'),
  'evidence-review': () => import('../components/employees/EvidenceReviewModalEntry.jsx'),
  'asset-form': () => import('../components/assets/AssetFormModalEntry.jsx'),
  'asset-detail': () => import('../components/assets/AssetDetailModalEntry.jsx'),
  'product-form': () => import('../products/ProductFormModalEntry.jsx'),
  'ropa-form': () => import('../components/privacy/ROPAFormModalEntry.jsx'),
  'ropa-detail': () => import('../components/privacy/ROPADetailModalEntry.jsx'),
  'incident-form': () => import('../components/incidents/IncidentFormModalEntry.jsx'),
  'incident-detail': () => import('../components/incidents/IncidentDetailModalEntry.jsx'),
  'incident-exercise-form': () =>
    import('../components/incidents/IncidentExerciseFormModalEntry.jsx'),
  'incident-exercise-detail': () =>
    import('../components/incidents/IncidentExerciseDetailModalEntry.jsx'),
  'mr-modal': () => import('../components/managementreview/ManagementReviewModalEntry.jsx'),
};

export const MODAL_REGISTRY = Object.fromEntries(
  Object.entries(MODAL_LOADERS).map(([type, loader]) => [type, lazy(loader)])
);

/**
 * Kick off a registered modal's chunk fetch ahead of the push that will
 * actually need it, without changing its bundle boundary (still one chunk
 * per modal type). A dynamic `import()` for a given module specifier is
 * cached by the browser/bundler after the first call, so calling the loader
 * here and letting `MODAL_REGISTRY`'s `lazy()` wrapper call the *same*
 * loader again later (when the modal is actually pushed) resolves near-
 * instantly instead of starting a fresh network round trip.
 *
 * Why this exists (#5598, #6042, #6053): a modal opened from *within* an
 * already-open modal (e.g. AssetDetailModal's "Edit" pushing `asset-form`,
 * or its criticality badge pushing `bia-detail`) stacks a second lazy
 * modal on top of the first. Until that second modal's chunk arrives, the
 * DOM's topmost `.modal-box` — and any of its buttons sharing a label with
 * the modal underneath (e.g. both have a "Data & GDPR" tab, or both have
 * a "Delete" button) — is still the *lower* modal's, because the pushed
 * modal has no box in the DOM yet (only its Suspense-fallback backdrop).
 * A click that resolves during that window binds to the lower modal's
 * element and stays bound to it once the upper modal's chunk finishes
 * loading and covers it — the click then permanently misses, reported as
 * "visible/enabled/stable" yet intercepted by the new modal's backdrop.
 * Preloading the likely-next modal's chunk the moment its *trigger* modal
 * mounts (rather than waiting for the trigger action itself) gives the
 * fetch a head start of however long the user takes to act, closing the
 * window in practice. Call this from a modal that is a common launch point
 * for another lazy modal — see `AssetDetailModal.jsx` for the two call
 * sites this fixed.
 *
 * No-op for an unknown type. A failed fetch here (offline, a network blip)
 * is swallowed — this is a best-effort head start, not a requirement; the
 * actual push still works normally afterwards because React.lazy's own
 * `import()` call, made when the modal is actually rendered, is a fresh
 * attempt rather than a reuse of this one's rejected promise.
 */
export function preloadModal(type) {
  MODAL_LOADERS[type]?.()?.catch(() => {});
}
